/**
 * Customer Usage — client wrapper for the staff cross-org read.
 *
 * One call returns one row per practice: weekly results (checks, success rate,
 * dollars identified, issues caught, patient volume) + per-clinic breakdown +
 * WoW deltas. Engagement fields (adoption/DAU/active users/feature adoption)
 * are null until per-user/per-feature telemetry exists — the UI shows an honest
 * "not instrumented yet" state. Staff-only; backend re-checks the allowlist.
 */

import { httpsCallable } from 'firebase/functions'
import { functions } from '../config'

export type UsageStatus = 'active' | 'quiet' | 'dormant'

export interface CustomerUsageClinic {
  clinicId: string
  name: string
  operatingDays: number
  timezone: string | null
  checks: number
  successRate: number | null
  patients: number
}

export interface CustomerUsageRow {
  orgId: string
  name: string
  clinics: number
  activeClinics: number
  seats: number
  newUsers: number
  status: UsageStatus
  lastActivity: string | null
  contact: { name: string; email: string; role: string } | null
  // results (real)
  checks: number
  checksPrev: number
  successRate: number | null
  answeredRate: number | null
  dollarsIdentified: number
  issuesCaught: number
  patients: number
  patientsPrev: number
  // engagement (from usageEvents; forward-only, 0 until usage accrues)
  adoptionScore: number
  adoptionDelta: number | null
  avgDAU: number
  dauDelta: number | null
  activeUsers: number
  daysActive: number
  operatingDays: number
  featureAdoption: Record<string, number>
  activeUserList: { name: string; days: number; lastSeen: string; features: string[] }[]
  clinicList: CustomerUsageClinic[]
}

export interface CustomerUsageResult {
  generatedAt: string
  weekStart: string
  practiceCount: number
  engagementInstrumented: boolean
  summary: {
    activeThisWeek: number
    dormant: number
    quiet: number
    avgSuccessRate: number | null
    totalChecks: number
    totalPatients: number
  }
  rows: CustomerUsageRow[]
}

/** Fetch cross-org customer usage for the current + prior week. */
export async function listCustomerUsage(): Promise<CustomerUsageResult> {
  const fn = httpsCallable<Record<string, never>, CustomerUsageResult>(
    functions,
    'listCustomerUsageForStaff',
  )
  const res = await fn({})
  return res.data
}
